import apiClient from './api';
import { formatContactFullName } from '../utils/contactName';
import type {
  CreatePaymentRequest,
  DebtDirection,
  DebtEntry,
  DebtSummary,
  UpdatePaymentRequest,
} from '../types/debt';
import type { PagedResponse } from '../types/common';

// contactName is an FE-only display name synthesized from the first/last/middle name parts
// the backend returns — not sent by the backend itself.
function withDisplayName(summary: DebtSummary): DebtSummary {
  return {
    ...summary,
    contactName: formatContactFullName(summary) || undefined,
  };
}

export const debtService = {
  // Contacts with ledger history in the given direction, biggest debt first. Settled
  // contacts (balance = 0) are excluded unless includeSettled is true.
  getSummary: async (
    companyId: number,
    direction: DebtDirection,
    includeSettled: boolean,
    page: number,
    size: number
  ): Promise<PagedResponse<DebtSummary>> => {
    const response = await apiClient.get<PagedResponse<DebtSummary>>('/debts/summary', {
      params: { companyId, direction, includeSettled, page, size },
    });
    return { ...response.data, content: response.data.content.map(withDisplayName) };
  },

  // A contact's full ledger for one direction, newest first, with a running balance
  getContactLedger: async (
    contactId: number,
    direction: DebtDirection,
    page: number,
    size: number
  ): Promise<PagedResponse<DebtEntry>> => {
    const response = await apiClient.get<PagedResponse<DebtEntry>>(`/debts/contacts/${contactId}/ledger`, {
      params: { direction, page, size },
    });
    return response.data;
  },

  getBalance: async (contactId: number, direction: DebtDirection): Promise<number> => {
    const response = await apiClient.get<number>(`/debts/contacts/${contactId}/balance`, {
      params: { direction },
    });
    return response.data;
  },

  recordPayment: async (request: CreatePaymentRequest): Promise<DebtEntry> => {
    const response = await apiClient.post<DebtEntry>('/debts/payments', request);
    return response.data;
  },

  updatePayment: async (paymentId: number, request: UpdatePaymentRequest): Promise<DebtEntry> => {
    const response = await apiClient.put<DebtEntry>(`/debts/payments/${paymentId}`, request);
    return response.data;
  },

  deletePayment: async (paymentId: number): Promise<void> => {
    await apiClient.delete(`/debts/payments/${paymentId}`);
  },
};
